import React from 'react';
import { motion } from 'framer-motion';

const defaultTransition = { duration: 1.2, ease: [0.25, 1, 0.5, 1] };
const scrollRevealEasing = { duration: 0.8, ease: [0.25, 1, 0.5, 1] };

const Navigation = () => (
  <nav 
    className="fixed top-0 left-0 w-full h-[70px] md:h-[80px] z-50 flex items-center justify-between px-4 md:px-8 bg-[rgba(17,17,17,0.7)] backdrop-blur-[20px] apple-transition border-b border-white/5"
    style={{ transform: 'translate3d(0,0,0)' }}
  >
    <div className="flex items-center">
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="bg-transparent border-none p-0 cursor-pointer focus:outline-none"
        aria-label="Back to top"
        style={{ transition: 'opacity 0.4s cubic-bezier(0.25, 1, 0.5, 1)' }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        <img 
          src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/0ed106a94d023e4deb32354ceaeeacdf.PNG" 
          alt="CLAXXIQ" 
          className="h-[32px] md:h-[44px] w-auto select-none"
          decoding="async"
          fetchPriority="high"
          draggable="false"
        />
      </button>
    </div>
    
    <div className="flex items-center gap-4 md:gap-8">
      <a 
        href="#spaces" 
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('spaces')?.scrollIntoView({ behavior: 'smooth' });
        }}
        className="hidden md:block text-[#A1A1A6] hover:text-white apple-transition text-sm font-medium cursor-pointer"
      >
        The Studio
      </a>
      <a 
        href="#software" 
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('software')?.scrollIntoView({ behavior: 'smooth' });
        }}
        className="hidden md:block text-[#A1A1A6] hover:text-white apple-transition text-sm font-medium cursor-pointer"
      >
        SOS
      </a>
      <a 
        href="https://booking.bayareapodcast.studio/"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-white text-onyx px-[16px] py-[10px] md:px-[24px] md:py-[12px] flex items-center justify-center font-[600] text-[13px] md:text-sm tracking-tighter apple-transition hover:bg-[#e0e0e0] active:scale-[0.98] rounded-[8px] no-underline"
        style={{ willChange: 'transform' }}
      >
        Book Studio
      </a>
    </div>
  </nav>
);

const Hero = () => (
  <section className="relative min-h-[100svh] w-full bg-onyx flex items-center justify-center overflow-hidden pt-[80px]">
    <div className="absolute inset-0 bg-grid-pattern bg-grid-size pointer-events-none z-0" />
    
    <div 
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[800px] md:h-[800px] bg-white rounded-full blur-[120px] animate-pulse-glow pointer-events-none z-0" 
      style={{ willChange: 'opacity' }} 
    />

    <div className="relative z-10 max-w-[800px] text-center px-6">
      <motion.h1 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...defaultTransition, delay: 0 }}
        className="text-[clamp(36px,8vw,80px)] leading-[1.05] mb-6 font-[800] tracking-tighter text-white relative z-20"
        style={{ willChange: 'transform, opacity' }}
      >
        Premium recording spaces and the software that runs them.
      </motion.h1>
      
      <motion.p 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...defaultTransition, delay: 0.1 }}
        className="text-[#C7C7CC] text-[16px] md:text-[20px] font-medium leading-[1.6] max-w-[560px] mx-auto mb-10 md:mb-12 relative z-20"
        style={{ willChange: 'transform, opacity' }}
      >
        We handle the entire production process from start to finish. No technical headaches. No mismatched tools. Just effortless content creation.
      </motion.p>
      
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...defaultTransition, delay: 0.2 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 relative z-20 w-full"
        style={{ willChange: 'transform, opacity' }}
      >
        <button 
          onClick={() => document.getElementById('spaces')?.scrollIntoView({ behavior: 'smooth' })}
          className="w-full sm:w-auto px-6 py-4 md:px-8 md:py-4 border border-[#A1A1A6] bg-transparent text-white font-[800] text-sm tracking-tighter apple-transition hover:bg-white hover:text-onyx hover:border-white active:scale-[0.98] rounded-[8px] relative z-20 cursor-pointer"
          style={{ willChange: 'transform, background-color, color' }}
        >
          Book Flagship Studio
        </button>
        <button 
          onClick={() => document.getElementById('software')?.scrollIntoView({ behavior: 'smooth' })}
          className="w-full sm:w-auto px-6 py-4 md:px-8 md:py-4 border border-[#A1A1A6] bg-transparent text-white font-[800] text-sm tracking-tighter apple-transition hover:bg-white hover:text-onyx hover:border-white active:scale-[0.98] rounded-[8px] relative z-20 cursor-pointer"
          style={{ willChange: 'transform, background-color, color' }}
        >
          View Software Suite
        </button>
      </motion.div>
    </div>
  </section>
);

const Catalyst = () => (
  <section className="relative z-10 h-auto bg-[#FFFFFF] py-[80px] md:py-[160px] px-6 shadow-[0_30px_60px_rgba(0,0,0,0.6)]">
    <div className="w-full mx-auto flex flex-col items-center text-center max-w-[800px]">
      <motion.img 
        src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/e394b5ca4a556fbe12bfef365972254d.PNG"
        alt="CLAXXIQ Hallmark"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ ...scrollRevealEasing, delay: 0 }}
        className="h-[40px] md:h-[56px] w-auto mb-[24px] md:mb-[40px] select-none"
        loading="lazy"
        decoding="async"
        style={{ willChange: 'transform, opacity' }}
      />

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ ...scrollRevealEasing, delay: 0.1 }}
        className="text-[clamp(28px,5vw,52px)] font-[800] tracking-[-0.04em] leading-[1.1] text-[#111111] mb-[24px] md:mb-[32px]"
        style={{ textWrap: 'balance', willChange: 'transform, opacity' }}
      >
        We build the rooms and write the software.
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ ...scrollRevealEasing, delay: 0.2 }}
        className="text-[#515154] text-[17px] md:text-[20px] leading-[1.65] font-[400]"
        style={{ textWrap: 'balance', willChange: 'transform, opacity' }}
      >
        Claxxiq controls the entire production experience from start to finish. We design premium physical recording sets and develop the native applications that run inside them. Because the spaces and the tools are built together as one unified system, the technology becomes completely invisible. Creators can focus entirely on their message, and studio owners get complete control over the room.
      </motion.p>
    </div>
  </section>
);

const SpacesPillar = () => (
  <section id="spaces" className="relative w-full min-h-[100svh] overflow-hidden bg-onyx z-10 shadow-[0_40px_80px_rgba(0,0,0,0.8)]">
    
    <motion.div
      initial={{ opacity: 0, scale: 1.06, filter: 'blur(20px)' }}
      whileInView={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 2.4, ease: [0.25, 1, 0.5, 1] }}
      className="absolute inset-0 w-full h-full z-0"
    >
      <video 
        src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/6676d5348900f3e9131a331c82ad8a03.mp4"
        autoPlay 
        loop 
        muted 
        playsInline
        disablePictureInPicture
        preload="auto"
        className="w-full h-full object-cover"
        style={{
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 25%)',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 25%)',
          transform: 'translateZ(0)'
        }}
      />
    </motion.div>

    <div 
      className="absolute inset-0 z-[1] pointer-events-none mix-blend-overlay opacity-[0.15]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        transform: 'translateZ(0)',
        willChange: 'transform'
      }}
    />

    <div 
      className="absolute inset-0 z-[2] pointer-events-none"
      style={{
        background: 'linear-gradient(80deg, #111111 0%, rgba(17,17,17,0.85) 35%, transparent 75%), linear-gradient(to top, #111111 0%, rgba(17,17,17,0.4) 25%, transparent 60%)'
      }}
    />

    <div className="relative z-[3] px-[6vw] pb-[60px] md:pb-[100px] flex flex-col justify-end min-h-[100svh]">
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 1.4, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
      >
        <h2 className="text-white font-[800] text-[clamp(36px,8vw,84px)] tracking-[-0.05em] leading-[1.05] mb-[16px] md:mb-[24px]">
          Bay Area Podcast Studio
        </h2>
        
        <p className="text-[rgba(255,255,255,0.95)] max-w-[640px] text-[16px] md:text-[20px] leading-[1.5] mb-[32px] md:mb-[48px]">
          Our physical recording space in Fremont, California. Built on a single rule: The "Touch Nothing" Standard. You walk in, sit down, and speak. We handle the rest from start to finish so you look undeniable and sound flawless.
        </p>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-[16px] md:gap-[24px] w-full max-w-[640px]">
          <a 
            href="https://booking.bayareapodcast.studio/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto bg-[#FFFFFF] text-[#000000] font-[700] px-[24px] py-[16px] md:px-[36px] md:py-[18px] rounded-[8px] hover:scale-[1.05] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] inline-block text-center no-underline"
            style={{ transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)', willChange: 'transform' }}
          >
            Book the Studio
          </a>

          <a 
            href="https://reviews.bayareapodcast.studio/wall-of-love-1759820467754"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto flex items-center justify-center gap-[8px] md:gap-[10px] px-[20px] py-[14px] md:px-[24px] md:py-[12px] cursor-pointer hover:scale-[1.02] no-underline"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              borderRadius: '40px',
              transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
              willChange: 'transform'
            }}
          >
            <span className="text-[#F5C518] text-[16px] tracking-widest leading-none mt-[-2px]">★★★★★</span>
            <span className="text-white font-[600] text-[14px] md:text-[15px]">5.0 Google Rating</span>
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const StudioTimerDemo = () => (
  <div className="w-full max-w-5xl mx-auto group perspective-[1000px]">
    <div 
      className="relative w-full aspect-video bg-zinc-900 rounded-[2rem] md:rounded-[2.5rem] p-[8px] md:p-[12px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] ring-1 ring-white/10 sm:group-hover:-translate-y-3 sm:group-hover:shadow-[0_45px_80px_-15px_rgba(0,0,0,0.95)]"
      style={{ transition: 'all 0.8s cubic-bezier(0.25, 1, 0.5, 1)', willChange: 'transform, box-shadow' }}
    >
      <div className="absolute top-0 left-0 w-full h-[8px] md:h-[12px] flex items-center justify-center gap-2 z-20 pointer-events-none">
        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] ring-[0.5px] ring-white/5" />
        <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-black rounded-full opacity-80" />
      </div>
      <div className="w-full h-full bg-zinc-950 rounded-[1.5rem] md:rounded-[1.8rem] overflow-hidden relative">
        <video 
          src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/12dacb60cc5bf30e7a79da4ef6359f0f.mp4"
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-transparent pointer-events-none" />
      </div>
    </div>
  </div>
);

const StudioSignDemo = () => (
  <div className="w-full max-w-5xl mx-auto group perspective-[1000px]">
    <div 
      className="relative w-full aspect-video bg-zinc-900 rounded-[2rem] md:rounded-[2.5rem] p-[8px] md:p-[12px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] ring-1 ring-white/10 sm:group-hover:-translate-y-3 sm:group-hover:shadow-[0_45px_80px_-15px_rgba(0,0,0,0.95)]"
      style={{ transition: 'all 0.8s cubic-bezier(0.25, 1, 0.5, 1)', willChange: 'transform, box-shadow' }}
    >
      <div className="absolute top-0 left-0 w-full h-[8px] md:h-[12px] flex items-center justify-center gap-2 z-20 pointer-events-none">
        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] ring-[0.5px] ring-white/5" />
        <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-black rounded-full opacity-80" />
      </div>
      <div className="w-full h-full bg-zinc-950 rounded-[1.5rem] md:rounded-[1.8rem] overflow-hidden relative">
        <video 
          src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/8516bb5c9c8757ff73d2e6c1554c9adb.mp4"
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-transparent pointer-events-none" />
      </div>
    </div>
  </div>
);

const SoftwarePillar = () => (
  <section id="software" className="relative z-[1] bg-[#F5F5F7] py-[80px] md:py-[160px] px-[6vw]">
    <div className="max-w-[1200px] mx-auto relative z-[1]">
      
      <div className="max-w-[800px] mx-auto text-center mb-[80px] md:mb-[160px]">
        <h2 className="text-[#111111] font-[800] text-[clamp(32px,6vw,64px)] tracking-[-0.04em] leading-[1.1] mb-[16px] md:mb-[24px]">
          One system. Two divisions.
        </h2>
        <p className="text-[#515154] text-[16px] md:text-[20px] leading-[1.5]">
          Claxxiq runs on two connected pieces. The Physical Studio operates high-end recording showrooms that dominate local markets and serve as our real-world testing labs. SOS (Studio Operating System) is the family of software applications built from those labs to automate booking, room control, and video workflows worldwide.
        </p>
      </div>

      {/* ROW 1: StudioTimer */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[60px] lg:gap-[80px] items-center mb-[80px] md:mb-[160px]">
        <div className="order-1 lg:order-1">
          <h3 className="text-[#111111] text-[32px] md:text-[40px] font-[700] mb-[16px]">
            StudioTimer
          </h3>
          <p className="text-[#515154] leading-[1.6] mb-[24px] md:mb-[32px] text-[16px] md:text-[18px]">
            A clean countdown clock built for the recording room. Running on a tablet anywhere in the space, it anchors the pacing of the session so clients finish exactly on time without you ever having to interrupt them. No time anxiety. Just pure focus.
          </p>
          <a 
            href="https://www.studiotimer.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto bg-[#111111] text-[#FFFFFF] rounded-[8px] px-[24px] md:px-[28px] py-[14px] font-[600] inline-block text-center no-underline"
            style={{ transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)', willChange: 'transform, opacity' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(0.98)';
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.opacity = '1';
            }}
          >
            View App ↗
          </a>
        </div>

        <div className="order-2 lg:order-2">
          <StudioTimerDemo />
        </div>
      </div>

      {/* ROW 2: StudioSign */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[60px] lg:gap-[80px] items-center">
        <div className="order-2 lg:order-1">
          <StudioSignDemo />
        </div>

        <div className="order-1 lg:order-2">
          <h3 className="text-[#111111] text-[32px] md:text-[40px] font-[700] mb-[16px]">
            StudioSign
          </h3>
          <p className="text-[#515154] leading-[1.6] mb-[24px] md:mb-[32px] text-[16px] md:text-[18px]">
            A smart room status system run on simple tablets. It automatically communicates with your booking calendar to display whether a set is live, standing by, or wrapping up so no one ever walks in and ruins a recording.
          </p>
          <a 
            href="https://www.studiosign.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto bg-[#111111] text-[#FFFFFF] rounded-[8px] px-[24px] md:px-[28px] py-[14px] font-[600] inline-block text-center no-underline"
            style={{ transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)', willChange: 'transform, opacity' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(0.98)';
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.opacity = '1';
            }}
          >
            View App ↗
          </a>
        </div>
      </div>

    </div>
  </section>
);

const Footer = () => (
  <footer 
    className="bg-[#050505] pt-[80px] md:pt-[120px] px-[6vw] pb-[60px] relative text-[#FFFFFF]"
    style={{ boxShadow: 'inset 0 40px 80px -20px rgba(0,0,0,1)' }}
  >
    <div className="flex flex-col items-center max-w-[800px] mx-auto mb-[60px] md:mb-[100px] text-center">
      <h2 className="text-[#FFFFFF] text-[clamp(32px,6vw,56px)] font-[800] tracking-[-0.03em] mb-[16px] md:mb-[24px]">
        Content creation, completely effortless.
      </h2>
      <p className="text-[#A1A1A6] text-[16px] md:text-[20px] leading-[1.5] mb-[32px] md:mb-[40px]">
        We remove the complications from the creator economy. Whether you are recording inside our physical rooms or utilizing our software tools on your own floor, the focus remains exactly where it belongs: on your message.
      </p>
      <div className="flex flex-col sm:flex-row gap-[16px] w-full sm:w-auto">
        <a 
          href="https://booking.bayareapodcast.studio/"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto bg-[#FFFFFF] text-[#050505] font-[600] px-[24px] py-[14px] md:px-[28px] md:py-[14px] rounded-[8px] inline-block text-center no-underline"
          style={{ transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)', willChange: 'transform' }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          Book the Studio
        </a>
        <button 
          onClick={() => document.getElementById('software')?.scrollIntoView({ behavior: 'smooth' })}
          className="w-full sm:w-auto bg-transparent border border-[rgba(255,255,255,0.2)] text-[#FFFFFF] font-[600] px-[24px] py-[14px] md:px-[28px] md:py-[14px] rounded-[8px] cursor-pointer"
          style={{ transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)', willChange: 'transform, background-color' }}
          onMouseEnter={(e) => { 
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; 
            e.currentTarget.style.transform = 'scale(0.98)';
          }}
          onMouseLeave={(e) => { 
            e.currentTarget.style.backgroundColor = 'transparent'; 
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          View Software Suite
        </button>
      </div>
    </div>

    <div className="max-w-[1200px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[40px] pb-[60px] md:pb-[80px] border-b border-[rgba(255,255,255,0.1)] mb-[60px] md:mb-[80px]">
      <div className="flex flex-col items-start">
        <img 
          src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/98cab0ee1df11baf9b9a1fd2afb13f25.PNG" 
          alt="CLAXXIQ Hallmark" 
          className="h-[40px] md:h-[56px] w-auto mb-[24px] select-none"
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="flex flex-col">
        <h4 className="text-[#A1A1A6] text-[11px] md:text-[12px] font-[700] tracking-[0.1em] mb-[20px] md:mb-[24px]">FLAGSHIP STUDIO</h4>
        <div className="flex flex-col gap-[16px] items-start">
          <a href="https://www.bayareapodcast.studio/" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">Bay Area Podcast Studio</a>
        </div>
      </div>

      <div className="flex flex-col">
        <h4 className="text-[#A1A1A6] text-[11px] md:text-[12px] font-[700] tracking-[0.1em] mb-[20px] md:mb-[24px]">SOS</h4>
        <div className="flex flex-col gap-[16px] items-start">
          <a href="https://www.studiotimer.app/" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">StudioTimer</a>
          <a href="https://www.studiosign.app/" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">StudioSign</a>
        </div>
      </div>

      <div className="flex flex-col">
        <h4 className="text-[#A1A1A6] text-[11px] md:text-[12px] font-[700] tracking-[0.1em] mb-[20px] md:mb-[24px]">OPERATIONS</h4>
        <div className="flex flex-col gap-[16px] items-start">
          <a href="https://formrobin.com/f/93gd723" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">Contact us</a>
          <a href="https://gettermscdn.com/view/F5qfJ/privacy/en-us" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">Privacy Policy</a>
          <a href="https://gettermscdn.com/view/F5qfJ/terms-of-service/en-us" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">Terms of Service</a>
          <a href="https://gettermscdn.com/view/F5qfJ/acceptable-use/en-us" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">Acceptable Use Policy</a>
          <a href="https://gettermscdn.com/view/F5qfJ/cookies/en-us" target="_blank" rel="noopener noreferrer" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300">Cookie Policy</a>
          <button data-gt-cookie-widget-show="true" className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300 bg-transparent border-none p-0 cursor-pointer text-left focus:outline-none">Manage Cookie Preferences</button>
          <button 
            onClick={() => {
              const aswBtn = document.querySelector('.asw-menu-btn');
              if (aswBtn) aswBtn.click();
            }}
            className="text-[#FFFFFF] text-[14px] md:text-[15px] font-[500] hover:text-[#A1A1A6] transition-colors duration-300 bg-transparent border-none p-0 cursor-pointer text-left focus:outline-none"
          >
            Manage Accessibility
          </button>
        </div>
      </div>
    </div>

    <div className="flex flex-col items-center">
      <img 
        src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/0ed106a94d023e4deb32354ceaeeacdf.PNG" 
        alt="CLAXXIQ Wordmark" 
        className="h-[24px] md:h-[28px] w-auto mb-[24px] select-none"
        loading="lazy"
        decoding="async"
      />
      <div className="text-[#8E8E93] text-[12px] md:text-[13px] text-center">
        © 2026 CLAXXIQ LLC.
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="w-full bg-onyx min-h-screen">
      <Navigation />
      <Hero />
      <Catalyst />
      <SpacesPillar />
      <SoftwarePillar />
      <Footer />
    </div>
  );
}

export default App;